// firestore.ts
import { collection, getDocs, query, where, orderBy } from "firebase/firestore";
import { Collections, Guide, SongRequest, Status } from "../../types/type";
import { db } from "../../firebase/firebaseConfig";
import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import { useGuideSearch } from "../../hooks/search/useGuideSearch";
import { inferGuide } from "../../api/aws/infer";
import { useToast } from "../../hooks/useToast";

export const fetchSongRequests = async (): Promise<SongRequest[]> => {
  try {
    const q = query(
      collection(db, Collections.SONG_REQUEST),
      where("status", "==", Status.PENDING)
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(
      (doc) => ({ ...doc.data(), songRequestId: doc.id } as SongRequest)
    );
  } catch (error) {
    console.error("Error fetching song requests:", error);
    throw error;
  }
};

const Container = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 24px;
  color: #333;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 24px;
`;

const Th = styled.th`
  padding: 12px;
  text-align: left;
  background-color: #f5f5f5;
  border-bottom: 2px solid #ddd;
`;

const Td = styled.td`
  padding: 12px;
  border-bottom: 1px solid #ddd;
`;

const Button = styled.button<{ variant?: "primary" | "secondary" }>`
  padding: 8px 16px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  background-color: ${(props) =>
    props.variant === "primary" ? "#1976d2" : "#f5f5f5"};
  color: ${(props) => (props.variant === "primary" ? "white" : "#333")};
  &:hover {
    opacity: 0.9;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  padding: 24px;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-bottom: 16px;
`;

const GuideItem = styled.div<{ isSelected: boolean }>`
  padding: 12px;
  border: 1px solid ${(props) => (props.isSelected ? "#1976d2" : "#ddd")};
  border-radius: 4px;
  margin-bottom: 8px;
  cursor: pointer;
  background-color: ${(props) => (props.isSelected ? "#f0f7ff" : "white")};
  &:hover {
    background-color: ${(props) => (props.isSelected ? "#f0f7ff" : "#f5f5f5")};
  }
`;

const InferComponent: React.FC = () => {
  const [songRequests, setSongRequests] = useState<SongRequest[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<SongRequest | null>(
    null
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [selectedGuide, setSelectedGuide] = useState<Guide | null>(null);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { fireToast } = useToast();

  const { searchResult: guides } = useGuideSearch(
    searchQuery,
    setIsTyping,
    selectedRequest?.existingArtist?.isMale
      ? "male"
      : selectedRequest?.existingArtist?.isMale === false
      ? "female"
      : "all"
  );

  useEffect(() => {
    const loadSongRequests = async () => {
      try {
        const requests = await fetchSongRequests();
        setSongRequests(requests);
      } catch (error) {
        fireToast({ content: "Failed to load song requests" });
      }
    };

    loadSongRequests();
  }, []);

  const handleGuideSelection = (guide: Guide) => {
    setSelectedGuide(guide);
  };

  const handleInferRequest = async () => {
    if (
      !selectedRequest ||
      !selectedGuide ||
      !selectedRequest.existingArtist?.modelName
    )
      return;

    setLoading(true);
    try {
      const params = {
        requestId: selectedRequest.songRequestId,
        requestUserId: selectedRequest.userId,
        modelName: selectedRequest.existingArtist.modelName,
        isMaleSinger: selectedGuide.isMale,
        model: "default",
        guideName: selectedGuide.guideTitle,
        guideId: selectedGuide.id,
        requestAt: new Date().toISOString(),
      };

      const response = await inferGuide(params);

      if (response.success) {
        fireToast({ content: "Guide inference request sent successfully" });
        setShowModal(false);
        // Remove the processed request from the list
        setSongRequests((prev) =>
          prev.filter(
            (req) => req.songRequestId !== selectedRequest.songRequestId
          )
        );
      } else {
        fireToast({ content: "Failed to send inference request" });
      }
    } catch (error) {
      fireToast({ content: "An error occurred while processing the request" });
    } finally {
      setLoading(false);
    }
  };

  const isRequestEnabled = (request: SongRequest) => {
    return request.existingArtist?.modelName != null;
  };

  const sortedSongRequests = useMemo(() => {
    return [...songRequests].sort((a, b) => {
      // existingArtist.modelName이 있는 요청을 상위로 정렬
      if (a.existingArtist?.modelName && !b.existingArtist?.modelName)
        return -1;
      if (!a.existingArtist?.modelName && b.existingArtist?.modelName) return 1;

      // requestAt 기준으로 최신순 정렬
      const dateA = new Date(a.requestAt).getTime();
      const dateB = new Date(b.requestAt).getTime();
      return dateB - dateA;
    });
  }, [songRequests]);

  return (
    <Container>
      <Title>Song Request Manager</Title>

      <Table>
        <thead>
          <tr>
            <Th>Song Title</Th>
            <Th>Artist Name</Th>
            <Th>User Name</Th>
            <Th>Model Name</Th>
            <Th>Action</Th>
          </tr>
        </thead>
        <tbody>
          {sortedSongRequests.map((request) => (
            <tr
              key={request.songRequestId}
              style={{
                backgroundColor: request.existingArtist?.modelName
                  ? "#f8f9fa"
                  : "transparent",
              }}
            >
              <Td>{request.songTitle}</Td>
              <Td>{request.userName}</Td>
              <Td>{request.status}</Td>
              <Td>{request.existingArtist?.modelName || "N/A"}</Td>
              <Td>
                <Button
                  onClick={() => {
                    setSelectedRequest(request);
                    setShowModal(true);
                    // 모달 열 때 검색어 초기화
                    setSearchQuery("");
                  }}
                  disabled={!isRequestEnabled(request)}
                  style={{
                    opacity: isRequestEnabled(request) ? 1 : 0.5,
                    cursor: isRequestEnabled(request)
                      ? "pointer"
                      : "not-allowed",
                  }}
                >
                  Select Guide
                </Button>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>

      {showModal && selectedRequest && (
        <Modal onClick={() => setShowModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginBottom: "16px", fontSize: "20px" }}>
              Select Guide for {selectedRequest.songTitle}
              {selectedRequest.existingArtist && (
                <div
                  style={{ fontSize: "14px", color: "#666", marginTop: "8px" }}
                >
                  Model: {selectedRequest.existingArtist.modelName}
                </div>
              )}
            </h2>

            <SearchInput
              type="text"
              placeholder={`Search ${
                selectedRequest.existingArtist?.isMale ? "male" : "female"
              } guides...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            <div style={{ marginBottom: "16px" }}>
              {guides.length > 0 ? (
                guides.map((guide) => (
                  <GuideItem
                    key={guide.id}
                    isSelected={selectedGuide?.id === guide.id}
                    onClick={() => handleGuideSelection(guide)}
                  >
                    <div style={{ fontWeight: 500 }}>{guide.guideTitle}</div>
                    <div style={{ fontSize: "14px", color: "#666" }}>
                      {guide.isMale ? "Male" : "Female"}
                    </div>
                    {guide.driveId && (
                      <div style={{ fontSize: "12px", color: "#888" }}>
                        Drive ID: {guide.driveId}
                      </div>
                    )}
                  </GuideItem>
                ))
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    padding: "20px",
                    color: "#666",
                  }}
                >
                  No matching guides found for selected gender
                </div>
              )}
            </div>

            {/* ... modal buttons ... */}
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default InferComponent;
